import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import API_BASE from "../config/api";

/* BLANK defaults — a newly activated site shows its OWN (empty) content, not demo data. */
const DEMO = {
    name: "",
    contact_person: "",
    tagline: "",
    logo: "",
    cover: "",
    address: "",
    phone: "",
    email: "",
    website: "",
    about: "",
    nature_of_business: "",
    year_established: "",
    gst_no: "",
    legal_status: "",
    employees: "",
    annual_turnover: "",
    linkfacebook: "",
    linkinstagram: "",
    linklinkedin: "",
    products: [],
    gallery: [],
    videos: [],
};

const REVIEWS = [];

export default function BusinessWebsiteProfessionalTemplate() {
    const { domain } = useParams();
    const [real, setReal] = useState(null);
    const [loading, setLoading] = useState(true);

    const [tab, setTab] = useState("home");
    const [enq, setEnq] = useState({ open: false, product: "", name: "", mobile: "", email: "", message: "" });
    const [enqMsg, setEnqMsg] = useState(null);
    const [video, setVideo] = useState(null);
    const [detail, setDetail] = useState(null); // product detail popup
    const sliderRef = useRef(null);

    useEffect(() => {
        if (!domain) { setLoading(false); return; }
        fetch(`${API_BASE}/api/get_business_site.php?domain=${encodeURIComponent(domain)}`)
            .then((r) => r.json())
            .then((res) => { if (res.success && res.data) setReal(res.data); })
            .catch(() => { })
            .finally(() => setLoading(false));
    }, [domain]);

    const pick = (k) => (real && real[k] && String(real[k]).trim() !== "") ? real[k] : DEMO[k];
    const d = {
        name: pick("name"), contact_person: (real && real.name) ? real.name : DEMO.contact_person,
        tagline: pick("tagline"), cover: (real ? (real.cover || "") : DEMO.cover),
        logo: pick("logo"), address: pick("address"), phone: pick("phone"), email: pick("email"),
        website: pick("website"), about: pick("about"), nature_of_business: pick("nature_of_business"),
        year_established: pick("year_established"), gst_no: pick("gst_no"), legal_status: pick("legal_status"),
        employees: pick("employees"), annual_turnover: pick("annual_turnover"),
        linkfacebook: pick("linkfacebook"), linkinstagram: pick("linkinstagram"), linklinkedin: pick("linklinkedin"),
    };
    const products = (real && real.products && real.products.length) ? real.products : DEMO.products;
    const gallery = (real && real.gallery && real.gallery.length) ? real.gallery : DEMO.gallery;
    const videos = DEMO.videos;

    const grouped = useMemo(() => {
        const g = {}; products.forEach((p) => { const c = (p.category || "General").trim() || "General"; (g[c] = g[c] || []).push(p); }); return g;
    }, [products]);
    const categories = Object.keys(grouped);

    // auto-scroll featured slider
    useEffect(() => {
        const el = sliderRef.current; if (!el) return;
        const id = setInterval(() => {
            if (!el) return;
            if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 5) el.scrollTo({ left: 0, behavior: "smooth" });
            else el.scrollBy({ left: 240, behavior: "smooth" });
        }, 3000);
        return () => clearInterval(id);
    }, [products]);

    const slide = (dir) => { const el = sliderRef.current; if (el) el.scrollBy({ left: dir * 240, behavior: "smooth" }); };
    const scrollTo = (id, key) => { setTab(key); const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); };
    const openEnquiry = (p = "") => { setEnqMsg(null); setEnq({ open: true, product: p, name: "", mobile: "", email: "", message: p ? `I am interested in "${p}". Please share best price and details.` : "" }); };
    const openProduct = (p) => { if (p) setDetail(p); };

    const submitEnquiry = async (e) => {
        e.preventDefault(); setEnqMsg(null);
        try {
            const res = await fetch(`${API_BASE}/api/save_business_enquiry.php`, {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ domain: domain || "demo", product: enq.product, name: enq.name, mobile: enq.mobile, email: enq.email, message: enq.message }),
            });
            const r = await res.json();
            if (r.success) { setEnqMsg({ type: "success", text: "Enquiry sent! The supplier will contact you soon." }); setTimeout(() => setEnq((s) => ({ ...s, open: false })), 1600); }
            else setEnqMsg({ type: "danger", text: r.message || "Could not send." });
        } catch { setEnqMsg({ type: "danger", text: "Network error." }); }
    };

    if (loading) return <div className="bw-center"><div className="bw-spinner" /></div>;

    const initials = (d.name || "CN").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
    const years = /^\d{4}$/.test(String(d.year_established).trim()) ? Math.max(1, new Date().getFullYear() - parseInt(d.year_established, 10)) : null;
    const Stars = ({ n }) => <span className="bw-stars">{"★".repeat(n)}{"☆".repeat(5 - n)}</span>;

    const facts = [
        ["Nature of Business", d.nature_of_business, "🏭"],
        ["Legal Status of Firm", d.legal_status, "⚖️"],
        ["No. of Employees", d.employees, "👥"],
        ["Annual Turnover", d.annual_turnover, "📈"],
        ["Year of Establishment", d.year_established, "📅"],
        ["GST Number", d.gst_no, "🧾"],
    ].filter((f) => f[1]);

    return (
        <div className="bw">
            {/* HEADER */}
            <header id="home" className="bw-head">
                <div className="bw-head-inner">
                    {d.logo ? <img className="bw-logoimg" src={d.logo} alt={d.name} /> : <div className="bw-logobox">{initials}</div>}
                    <div className="bw-head-main">
                        <h1 className="bw-cname">{d.name || "Your Business Name"}</h1>
                        <div className="bw-badges">
                            {d.address && <span className="bw-loc">📍 {d.address}</span>}
                            <span className="bw-chip ok">✓ GST</span>
                            <span className="bw-chip ok">✓ TrustSEAL Verified</span>
                            <span className="bw-chip ok">✓ Payment Protected</span>
                            {years !== null && <span className="bw-chip">🏆 {years} yrs</span>}
                        </div>
                    </div>
                    <div className="bw-head-cta">
                        <a className="bw-btn bw-btn-outline" href={d.phone ? `tel:${d.phone}` : "#"}>📞 Call Now</a>
                        <button className="bw-btn bw-btn-primary" onClick={() => openEnquiry("")}>✈ Contact Supplier</button>
                    </div>
                </div>
            </header>

            {/* NAV */}
            <nav className="bw-nav">
                <div className="bw-nav-inner">
                    {[["home", "Home"], ["products", "Products & Services"], ["about", "About Us"], ["videos", "Videos"], ["photos", "Photos"], ["contact", "Contact Us"]].map(([k, label]) => (
                        <span key={k} className={tab === k ? "active" : ""} onClick={() => scrollTo(k === "home" ? "home" : k, k)}>{label}</span>
                    ))}
                    <div className="bw-nav-search"><input placeholder="🔍  Search Products / Services" /></div>
                </div>
            </nav>

            {/* HERO (agency style) */}
            <section className="bw-hero" style={{ backgroundImage: `linear-gradient(rgba(9,14,28,.82), rgba(9,14,28,.86))${d.cover ? `, url(${d.cover})` : ""}` }}>
                <div className="bw-hero-in">
                    <span className="bw-hero-eyebrow">{d.nature_of_business || "Welcome"}</span>
                    <h2 className="bw-hero-title">{d.tagline || d.name || "Your Business Website"}</h2>
                    <p className="bw-hero-desc">
                        {d.about && d.about.length > 200 ? d.about.slice(0, 200).trim() + "…" : d.about}
                    </p>
                    <div className="bw-hero-cta">
                        <button className="bw-btn bw-btn-accent" onClick={() => scrollTo("products", "products")}>Read More</button>
                        <button className="bw-btn bw-btn-primary" onClick={() => openEnquiry("")}>Contact Us</button>
                    </div>
                </div>
            </section>

            {/* STATS BAND */}
            {products.length > 0 && (
                <section className="bw-statband">
                    <div className="bw-statband-in">
                        <div><b>{products.length}+</b><span>Products & Services</span></div>
                        <div><b>{categories.length}+</b><span>Categories</span></div>
                        {years !== null && <div><b>{years}+</b><span>Years of Experience</span></div>}
                    </div>
                </section>
            )}

            {/* WHY CHOOSE US */}
            <section className="bw-wrap">
                <div className="bw-h-row"><h2 className="bw-h">Why Choose Us</h2></div>
                <div className="bw-why">
                    {[
                        ["✓", "Quality Assured", "Genuine, quality-checked products sourced from trusted brands."],
                        ["🤝", "Trusted Supplier", `${years ? years + "+ years" : "Years"} of experience serving satisfied customers.`],
                        ["⚡", "Quick Response", "Fast quotations and responsive customer support."],
                        ["🚚", "Reliable Service", "On-time delivery and dependable after-sales support."],
                    ].map(([ic, t, ds], i) => (
                        <div className="bw-whycard" key={i}>
                            <div className="bw-why-ic">{ic}</div>
                            <h3>{t}</h3>
                            <p>{ds}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ABOUT */}
            {d.about && (
                <section id="about" className="bw-wrap">
                    <div className="bw-card">
                        <h2 className="bw-h">About Us</h2>
                        <p className="bw-desc">{d.about}</p>
                    </div>
                </section>
            )}

            {/* FACTSHEET BAND */}
            {facts.length > 0 && (
                <section className="bw-wrap">
                    <div className="bw-factband">
                        {facts.map(([k, v, ic], i) => (
                            <div className="bw-fact" key={i}>
                                <div className="bw-fact-ic">{ic}</div>
                                <div><div className="bw-fact-k">{k}</div><div className="bw-fact-v">{v}</div></div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* CATEGORY RANGE GRID */}
            {categories.length > 0 && (
                <section id="products" className="bw-wrap">
                    <div className="bw-h-row"><h2 className="bw-h">Products &amp; Services</h2><span className="bw-viewall" onClick={() => openEnquiry("")}>Get Best Quote →</span></div>
                    <div className="bw-range">
                        {categories.map((c) => {
                            const list = grouped[c]; const first = list[0];
                            return (
                                <article className="bw-range-card" key={c}>
                                    <div className="bw-range-img" onClick={() => openProduct(first)}>{first?.image ? <img src={first.image} alt={c} /> : "Image"}</div>
                                    <div className="bw-range-body">
                                        <h3 className="bw-range-title">{c}</h3>
                                        <ul className="bw-range-list">
                                            {list.slice(0, 3).map((p, i) => (<li key={i} onClick={() => openProduct(p)}>{p.name}</li>))}
                                        </ul>
                                        <span className="bw-range-view" onClick={() => openProduct(first)}>View Details ❯</span>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                    <div className="bw-center-btn"><button className="bw-btn bw-btn-accent" style={{ width: "auto", padding: "11px 26px" }} onClick={() => openEnquiry("")}>View Complete Range</button></div>
                </section>
            )}

            {/* PRODUCT VIDEO */}
            {videos.length > 0 && (
                <section id="videos" className="bw-wrap">
                    <h2 className="bw-h">Product Video</h2>
                    <div className="bw-videos">
                        {videos.map((v, i) => (
                            <div className="bw-video" key={i}>
                                <button className="bw-video-thumb" onClick={() => setVideo(v.youtubeId)}>
                                    <img src={`https://img.youtube.com/vi/${v.youtubeId}/hqdefault.jpg`} alt={v.title} />
                                    <span className="bw-play">▶</span>
                                </button>
                                <p className="bw-video-t">{v.title}</p>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* PHOTOS */}
            {gallery.length > 0 && (
                <section id="photos" className="bw-wrap">
                    <h2 className="bw-h">Photos &amp; Gallery</h2>
                    <div className="bw-gallery">{gallery.map((g, i) => <div key={i} className="bw-gimg"><img src={g} alt={`photo-${i}`} /></div>)}</div>
                </section>
            )}

            {/* REVIEWS */}
            {REVIEWS.length > 0 && (
                <section className="bw-wrap">
                    <h2 className="bw-h">Customer Reviews</h2>
                    <div className="bw-reviews">
                        {REVIEWS.map((r, i) => (
                            <div className="bw-review" key={i}>
                                <div className="bw-review-top"><div className="bw-avatar">{r.name[0]}</div><div><div className="bw-review-name">{r.name}</div><Stars n={r.rating} /></div></div>
                                <p>{r.text}</p>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* FOOTER */}
            <footer id="contact" className="bw-footer">
                <div className="bw-footer-banner"><h2>Get in touch with us</h2></div>
                <div className="bw-footer-body">
                    <div className="bw-footer-col">
                        <h3>Our Company</h3>
                        <ul>
                            <li onClick={() => scrollTo("about", "about")}>About Us</li>
                            <li onClick={() => scrollTo("products", "products")}>Products &amp; Services</li>
                            <li onClick={() => scrollTo("videos", "videos")}>Videos</li>
                            <li onClick={() => scrollTo("photos", "photos")}>Photos</li>
                        </ul>
                    </div>
                    <div className="bw-footer-col">
                        <h3>Reach Us</h3>
                        <p className="bw-footer-name">📍 {d.name || "Your Business Name"}</p>
                        {d.address && <p>{d.address}</p>}
                        {d.contact_person && <p>👤 {d.contact_person}</p>}
                        {d.phone && <p>📞 {d.phone}</p>}
                        {d.email && <p>✉ {d.email}</p>}
                        <div className="bw-footer-cta">
                            <button className="bw-btn bw-btn-primary" onClick={() => openEnquiry("")}>✈ Send Enquiry</button>
                            <a className="bw-btn bw-btn-outline bw-btn-dark" href={d.phone ? `tel:${d.phone}` : "#"}>📞 Call Now</a>
                        </div>
                    </div>
                    <div className="bw-footer-col">
                        <h3>Connect</h3>
                        <div className="bw-footer-social">
                            {d.website && <a href={d.website} target="_blank" rel="noreferrer">🌐 Website</a>}
                            {d.linkfacebook && <a href={d.linkfacebook} target="_blank" rel="noreferrer">f Facebook</a>}
                            {d.linkinstagram && <a href={d.linkinstagram} target="_blank" rel="noreferrer">◎ Instagram</a>}
                            {d.linklinkedin && <a href={d.linklinkedin} target="_blank" rel="noreferrer">in LinkedIn</a>}
                        </div>
                    </div>
                </div>
                <div className="bw-footer-bottom">© {new Date().getFullYear()} {d.name || "Your Business"}. All Rights Reserved. &nbsp;·&nbsp; Powered by <b>Dial Nepal</b></div>
            </footer>

            {/* PRODUCT DETAIL POPUP */}
            {detail && (
                <div className="bw-overlay" onMouseDown={(e) => e.target.classList.contains("bw-overlay") && setDetail(null)}>
                    <div className="bw-pd">
                        <span className="bw-close bw-pd-close" onClick={() => setDetail(null)}>×</span>
                        <div className="bw-pd-grid">
                            <div className="bw-pd-img">
                                {detail.image ? <img src={detail.image} alt={detail.name} /> : <div className="bw-pd-noimg">No image</div>}
                            </div>
                            <div className="bw-pd-info">
                                {detail.category && <span className="bw-pd-cat">{detail.category}</span>}
                                <h2 className="bw-pd-name">{detail.name}</h2>
                                <div className="bw-pd-price">
                                    {detail.price ? <>₹ {detail.price}<small> / {detail.unit || "Unit"}</small></> : <em>Ask Price</em>}
                                </div>
                                {detail.description && (
                                    <div className="bw-pd-sec">
                                        <h4>Product Details</h4>
                                        <p>{detail.description}</p>
                                    </div>
                                )}
                                <table className="bw-pd-specs">
                                    <tbody>
                                        {detail.category && <tr><th>Category</th><td>{detail.category}</td></tr>}
                                        {detail.unit && <tr><th>Unit</th><td>{detail.unit}</td></tr>}
                                        {detail.price && <tr><th>Price</th><td>₹ {detail.price}</td></tr>}
                                        <tr><th>Availability</th><td>In Stock</td></tr>
                                    </tbody>
                                </table>
                                <div className="bw-pd-cta">
                                    <button className="bw-btn bw-btn-primary" onClick={() => { const n = detail.name; setDetail(null); openEnquiry(n); }}>✈ Send Enquiry</button>
                                    <a className="bw-btn bw-btn-outline" href={d.phone ? `tel:${d.phone}` : "#"}>📞 Call Now</a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ENQUIRY MODAL */}
            {enq.open && (
                <div className="bw-overlay" onMouseDown={(e) => e.target.classList.contains("bw-overlay") && setEnq((s) => ({ ...s, open: false }))}>
                    <div className="bw-dialog">
                        <div className="bw-dialog-head"><b>Send Enquiry {enq.product ? `— ${enq.product}` : ""}</b><span className="bw-close" onClick={() => setEnq((s) => ({ ...s, open: false }))}>×</span></div>
                        <form onSubmit={submitEnquiry} className="bw-dialog-body">
                            {enqMsg && <div className={`bw-alert ${enqMsg.type}`}>{enqMsg.text}</div>}
                            <input placeholder="Your Name *" value={enq.name} onChange={(e) => setEnq((s) => ({ ...s, name: e.target.value }))} required />
                            <input placeholder="Mobile Number *" value={enq.mobile} onChange={(e) => setEnq((s) => ({ ...s, mobile: e.target.value }))} required />
                            <input placeholder="Email" value={enq.email} onChange={(e) => setEnq((s) => ({ ...s, email: e.target.value }))} />
                            <textarea rows="3" placeholder="Your requirement" value={enq.message} onChange={(e) => setEnq((s) => ({ ...s, message: e.target.value }))} />
                            <button className="bw-btn bw-btn-primary" style={{ width: "100%" }}>Submit Enquiry</button>
                        </form>
                    </div>
                </div>
            )}

            {/* VIDEO MODAL */}
            {video && (
                <div className="bw-overlay" onMouseDown={(e) => e.target.classList.contains("bw-overlay") && setVideo(null)}>
                    <div className="bw-vdialog">
                        <span className="bw-close bw-vclose" onClick={() => setVideo(null)}>×</span>
                        <div className="bw-vframe"><iframe src={`https://www.youtube.com/embed/${video}?autoplay=1`} title="video" allow="autoplay; encrypted-media" allowFullScreen /></div>
                    </div>
                </div>
            )}

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Poppins:wght@600;700;800&display=swap');
                .bw{ --brand:#1e40af; --brand-2:#2563eb; --accent:#f6b100; --accent-d:#d99700; --ink:#0f172a; --muted:#64748b; --bg:#eef2f8; --border:#e5e9f2;
                    font-family:'Inter',system-ui,Arial,sans-serif; background:var(--bg); color:var(--ink); min-height:100vh; }
                .bw *{box-sizing:border-box;} .bw a{text-decoration:none;color:inherit;} .bw img{max-width:100%;display:block;}
                .bw h1,.bw h2,.bw h3{font-family:'Poppins',sans-serif;}
                .bw-center{display:flex;align-items:center;justify-content:center;height:100vh;background:var(--bg);}
                .bw-spinner{width:44px;height:44px;border:4px solid #dbe4ff;border-top-color:var(--brand-2);border-radius:50%;animation:bwspin 1s linear infinite;} @keyframes bwspin{to{transform:rotate(360deg);}}
                .bw-wrap{max-width:1180px;margin:22px auto 0;padding:0 16px;}
                .bw-h{font-size:19px;margin:0 0 16px;position:relative;padding-left:14px;}
                .bw-h:before{content:"";position:absolute;left:0;top:2px;bottom:2px;width:5px;border-radius:3px;background:linear-gradient(var(--brand-2),var(--accent));}
                .bw-h-row{display:flex;justify-content:space-between;align-items:center;} .bw-viewall{color:var(--brand-2);font-weight:700;font-size:13px;cursor:pointer;}
                .bw-card{background:#fff;border:1px solid var(--border);border-radius:14px;padding:20px 22px;box-shadow:0 2px 10px rgba(0,0,0,.04);}
                .bw-desc{font-size:14px;line-height:1.7;color:#374151;white-space:pre-line;margin:0;}

                .bw-head{background:#fff;border-bottom:1px solid var(--border);}
                .bw-head-inner{max-width:1180px;margin:0 auto;padding:16px;display:flex;align-items:center;gap:18px;flex-wrap:wrap;}
                .bw-logoimg,.bw-logobox{width:74px;height:74px;border-radius:14px;flex-shrink:0;object-fit:cover;box-shadow:0 6px 18px rgba(30,64,175,.18);}
                .bw-logobox{background:linear-gradient(135deg,var(--brand-2),var(--brand));display:flex;align-items:center;justify-content:center;color:#fff;font-weight:800;font-size:26px;font-family:'Poppins';}
                .bw-head-main{flex:1;min-width:240px;} .bw-cname{font-size:24px;font-weight:800;margin:0 0 6px;letter-spacing:-.3px;}
                .bw-badges{display:flex;flex-wrap:wrap;gap:8px 10px;font-size:12.5px;color:var(--muted);align-items:center;}
                .bw-loc{font-weight:600;color:#334155;}
                .bw-chip{background:#f1f5f9;padding:3px 10px;border-radius:20px;font-weight:600;} .bw-chip.ok{background:#e7f7ee;color:#0b8a4f;}
                .bw-stars{color:var(--accent);letter-spacing:1px;}
                .bw-head-cta{display:flex;gap:10px;}
                .bw-btn{padding:11px 20px;border-radius:10px;font-size:14px;font-weight:700;cursor:pointer;border:none;text-align:center;transition:.18s;font-family:'Inter';}
                .bw-btn-primary{background:linear-gradient(135deg,var(--brand-2),var(--brand));color:#fff;box-shadow:0 6px 16px rgba(37,99,235,.32);}
                .bw-btn-primary:hover{transform:translateY(-2px);}
                .bw-btn-outline{background:#fff;color:var(--brand);border:1.5px solid var(--brand);} .bw-btn-outline:hover{background:#eff4ff;}
                .bw-btn-dark{background:transparent;color:#fff;border-color:rgba(255,255,255,.5);margin-top:10px;} .bw-btn-dark:hover{background:rgba(255,255,255,.1);}
                .bw-btn-accent{background:linear-gradient(135deg,var(--accent),var(--accent-d));color:#3a2a00;box-shadow:0 6px 16px rgba(246,177,0,.35);} .bw-btn-accent:hover{transform:translateY(-2px);}

                .bw-nav{background:#fff;border-bottom:1px solid var(--border);position:sticky;top:0;z-index:20;box-shadow:0 3px 12px rgba(0,0,0,.05);}
                .bw-nav-inner{max-width:1180px;margin:0 auto;padding:0 16px;display:flex;align-items:center;gap:2px;overflow-x:auto;}
                .bw-nav-inner>span{padding:14px 18px;font-size:14px;font-weight:600;color:var(--muted);cursor:pointer;white-space:nowrap;border-bottom:3px solid transparent;transition:.15s;}
                .bw-nav-inner>span:hover{color:var(--brand-2);} .bw-nav-inner>span.active{color:var(--brand);border-bottom-color:var(--accent);}
                .bw-nav-search{margin-left:auto;} .bw-nav-search input{padding:9px 14px;border:1px solid var(--border);border-radius:22px;font-size:13px;min-width:240px;background:#f8fafc;}

                .bw-btn-wht{background:rgba(255,255,255,.14);color:#fff;border:1.5px solid rgba(255,255,255,.55);} .bw-btn-wht:hover{background:rgba(255,255,255,.24);}
                .bw-hero{background-size:cover;background-position:center;color:#fff;text-align:center;}
                .bw-hero-in{max-width:820px;margin:0 auto;padding:96px 20px;}
                .bw-hero-eyebrow{color:var(--accent);font-weight:700;letter-spacing:1px;font-size:16px;font-family:'Poppins';}
                .bw-hero-title{font-size:44px;font-weight:800;margin:10px 0 16px;line-height:1.12;}
                @media(max-width:640px){.bw-hero-title{font-size:30px;}}
                .bw-hero-desc{font-size:15px;line-height:1.75;opacity:.9;max-width:640px;margin:0 auto 28px;}
                .bw-hero-cta{display:flex;gap:14px;justify-content:center;flex-wrap:wrap;}
                .bw-hero-cta .bw-btn{padding:13px 30px;border-radius:30px;}
                .bw-statband{background:linear-gradient(135deg,var(--brand-2),var(--brand));color:#fff;}
                .bw-statband-in{max-width:1180px;margin:0 auto;padding:26px 16px;display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:16px;text-align:center;}
                .bw-statband b{display:block;font-family:'Poppins';font-size:34px;font-weight:800;color:var(--accent);}
                .bw-statband span{font-size:13px;opacity:.92;font-weight:600;}
                .bw-why{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:18px;}
                .bw-whycard{background:#fff;border:1px solid var(--border);border-radius:14px;padding:22px;text-align:center;transition:.2s;box-shadow:0 2px 10px rgba(0,0,0,.04);}
                .bw-whycard:hover{transform:translateY(-4px);box-shadow:0 14px 30px rgba(30,64,175,.12);border-color:#c9d8ff;}
                .bw-why-ic{width:56px;height:56px;margin:0 auto 12px;border-radius:14px;background:linear-gradient(135deg,#eef4ff,#dbe7ff);display:flex;align-items:center;justify-content:center;font-size:26px;}
                .bw-whycard h3{font-size:15px;margin:0 0 6px;} .bw-whycard p{font-size:13px;color:var(--muted);margin:0;line-height:1.55;}

                .bw-factband{background:#fff;border:1px solid var(--border);border-radius:14px;padding:8px;display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));box-shadow:0 2px 10px rgba(0,0,0,.04);}
                .bw-fact{display:flex;gap:12px;align-items:center;padding:14px 16px;border-right:1px solid var(--border);}
                .bw-fact-ic{font-size:24px;} .bw-fact-k{font-size:11.5px;color:var(--muted);} .bw-fact-v{font-size:13.5px;font-weight:700;}

                .bw-range{display:grid;grid-template-columns:repeat(auto-fill,minmax(230px,1fr));gap:18px;margin-top:4px;}
                .bw-range-card{background:#fff;border:1px solid var(--border);border-radius:14px;overflow:hidden;transition:.2s;box-shadow:0 2px 10px rgba(0,0,0,.04);}
                .bw-range-card:hover{box-shadow:0 12px 28px rgba(30,64,175,.12);transform:translateY(-3px);}
                .bw-range-img{height:190px;background:#eef1f6;display:flex;align-items:center;justify-content:center;color:var(--muted);cursor:pointer;overflow:hidden;}
                .bw-range-img img{width:100%;height:100%;object-fit:cover;transition:.3s;} .bw-range-card:hover .bw-range-img img{transform:scale(1.06);}
                .bw-range-body{padding:14px 16px;} .bw-range-title{font-size:15px;margin:0 0 8px;}
                .bw-range-list{list-style:none;padding:0;margin:0 0 10px;} .bw-range-list li{font-size:12.5px;color:#475569;padding:3px 0;cursor:pointer;border-bottom:1px dashed #eef1f6;}
                .bw-range-list li:hover{color:var(--brand-2);}
                .bw-range-view{color:var(--brand-2);font-size:12.5px;font-weight:700;cursor:pointer;}
                .bw-center-btn{text-align:center;margin-top:22px;}

                .bw-videos{display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:16px;}
                .bw-video-thumb{position:relative;width:100%;border:none;padding:0;border-radius:12px;overflow:hidden;cursor:pointer;display:block;}
                .bw-video-thumb img{width:100%;height:150px;object-fit:cover;}
                .bw-play{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:20px;color:#fff;background:rgba(0,0,0,.35);}
                .bw-video-t{font-size:13px;font-weight:600;margin:8px 0 0;}

                .bw-gallery{display:grid;grid-template-columns:repeat(auto-fill,minmax(190px,1fr));gap:14px;}
                .bw-gimg{border-radius:12px;overflow:hidden;height:170px;box-shadow:0 4px 12px rgba(0,0,0,.06);} .bw-gimg img{width:100%;height:100%;object-fit:cover;transition:.3s;} .bw-gimg:hover img{transform:scale(1.06);}

                .bw-reviews{display:grid;grid-template-columns:repeat(auto-fill,minmax(260px,1fr));gap:16px;}
                .bw-review{border:1px solid var(--border);border-radius:12px;padding:16px;background:#fff;box-shadow:0 2px 10px rgba(0,0,0,.04);}
                .bw-review-top{display:flex;align-items:center;gap:10px;margin-bottom:8px;}
                .bw-avatar{width:40px;height:40px;border-radius:50%;background:linear-gradient(135deg,var(--brand-2),var(--brand));color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;}
                .bw-review-name{font-weight:700;font-size:13.5px;} .bw-review p{font-size:13px;color:#475569;margin:0;}

                .bw-footer{margin-top:34px;background:var(--ink);color:#cbd5e1;}
                .bw-footer-banner{background:linear-gradient(135deg,var(--brand),var(--brand-2));text-align:center;padding:26px 16px;} .bw-footer-banner h2{color:#fff;margin:0;font-size:22px;}
                .bw-footer-body{max-width:1180px;margin:0 auto;padding:30px 16px;display:grid;grid-template-columns:1fr 1.3fr 1fr;gap:24px;}
                @media(max-width:760px){.bw-footer-body{grid-template-columns:1fr;}}
                .bw-footer-col h3{color:#fff;font-size:15px;margin:0 0 12px;} .bw-footer-col ul{list-style:none;padding:0;margin:0;} .bw-footer-col li{padding:5px 0;font-size:13px;cursor:pointer;} .bw-footer-col li:hover{color:#fff;}
                .bw-footer-col p{font-size:13px;margin:4px 0;} .bw-footer-name{font-weight:700;color:#fff;}
                .bw-footer-cta{margin-top:12px;} .bw-footer-social{display:flex;flex-direction:column;gap:8px;} .bw-footer-social a{background:rgba(255,255,255,.08);padding:8px 12px;border-radius:8px;font-size:13px;font-weight:600;}
                .bw-footer-bottom{border-top:1px solid rgba(255,255,255,.12);text-align:center;padding:16px;font-size:12px;} .bw-footer-bottom b{color:var(--accent);}

                .bw-overlay{position:fixed;inset:0;background:rgba(15,23,42,.6);z-index:20000;display:flex;align-items:center;justify-content:center;padding:24px 12px;overflow-y:auto;}
                .bw-dialog{background:#fff;width:100%;max-width:470px;border-radius:16px;box-shadow:0 24px 70px rgba(0,0,0,.35);overflow:hidden;align-self:flex-start;margin-top:44px;}
                .bw-dialog-head{display:flex;justify-content:space-between;align-items:center;padding:16px 18px;border-bottom:1px solid var(--border);background:linear-gradient(135deg,#f8faff,#eef3ff);}
                .bw-close{cursor:pointer;font-size:24px;line-height:1;color:#94a3b8;}
                .bw-dialog-body{padding:18px;display:flex;flex-direction:column;gap:11px;}
                .bw-dialog-body input,.bw-dialog-body textarea{padding:11px 13px;border:1px solid var(--border);border-radius:9px;font-size:14px;width:100%;font-family:'Inter';}
                .bw-dialog-body input:focus,.bw-dialog-body textarea:focus{outline:none;border-color:var(--brand-2);box-shadow:0 0 0 3px rgba(37,99,235,.15);}
                .bw-alert{padding:9px 12px;border-radius:8px;font-size:13px;} .bw-alert.success{background:#e7f7ee;color:#0b6e4f;} .bw-alert.danger{background:#fdecec;color:#c0392b;}
                .bw-vdialog{width:100%;max-width:860px;position:relative;} .bw-vframe{position:relative;padding-top:56.25%;border-radius:12px;overflow:hidden;background:#000;} .bw-vframe iframe{position:absolute;inset:0;width:100%;height:100%;border:0;}
                .bw-vclose{position:absolute;top:-34px;right:0;color:#fff;font-size:30px;}

                .bw-pd{background:#fff;width:100%;max-width:860px;border-radius:16px;box-shadow:0 24px 70px rgba(0,0,0,.35);position:relative;align-self:flex-start;margin-top:40px;overflow:hidden;}
                .bw-pd-close{position:absolute;top:10px;right:16px;z-index:2;}
                .bw-pd-grid{display:grid;grid-template-columns:1fr 1.2fr;gap:0;}
                @media(max-width:700px){.bw-pd-grid{grid-template-columns:1fr;}}
                .bw-pd-img{background:#f1f5f9;display:flex;align-items:center;justify-content:center;min-height:320px;padding:16px;}
                .bw-pd-img img{max-width:100%;max-height:420px;object-fit:contain;border-radius:8px;}
                .bw-pd-noimg{color:var(--muted);}
                .bw-pd-info{padding:26px 26px 22px;}
                .bw-pd-cat{display:inline-block;background:#eff4ff;color:var(--brand);font-size:11px;font-weight:700;padding:3px 10px;border-radius:20px;margin-bottom:8px;}
                .bw-pd-name{font-size:22px;margin:0 0 8px;}
                .bw-pd-price{font-size:22px;color:var(--brand);font-weight:800;margin-bottom:16px;} .bw-pd-price small{color:var(--muted);font-weight:500;font-size:14px;}
                .bw-pd-sec{margin-bottom:14px;} .bw-pd-sec h4{font-size:14px;margin:0 0 6px;} .bw-pd-sec p{font-size:13.5px;color:#374151;line-height:1.6;margin:0;white-space:pre-line;}
                .bw-pd-specs{width:100%;border-collapse:collapse;margin-bottom:18px;font-size:13px;}
                .bw-pd-specs th{text-align:left;color:var(--muted);font-weight:500;padding:7px 10px;width:40%;background:#f8fafc;border:1px solid var(--border);}
                .bw-pd-specs td{padding:7px 10px;font-weight:600;border:1px solid var(--border);}
                .bw-pd-cta{display:flex;gap:10px;flex-wrap:wrap;} .bw-pd-cta .bw-btn{flex:1;min-width:140px;}
            `}</style>
        </div>
    );
}
